import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import MenuSections from './menu-sections/MenuSections';
import Items from './items/Items';
import { useScreenSize } from '../../context/ScreenSizeContext';
import { useLocation } from '../../context/LocationContext';
import { useActiveSections, useAllMenuSections } from '../../context/MenuSectionsStore';
import CheckoutSideBar from './checkout-sidebar/CheckoutSidebar';
import { useSearch } from '../../context/SearchContext';
import { filterItemsOnSearch } from '../../utils/search';
import CartButton from './CartButton';
import { useNavigate } from 'react-router-dom';
import { createCartUrl } from '../../utils/linkBuilder';
import ErrorBoundary from '../../containers/ErrorBoundary';
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';
import SubsectionHeader from './menu-sections/SubsectionHeader';
import classNames from 'classnames';
import ExpandHeaderButton from '../../components/ExpandHeaderButton';
import { sortAllProducts } from '../../utils/sort';
import config from '../../config';
import { useBundleDefinitions } from '../../hooks/useBundleDefinitions';
import BundleConversionDialog from './BundleConversionDialog';

const Menu: FC = () => {
  const navigate = useNavigate();
  const { isPhone } = useScreenSize();
  const { location } = useLocation();
  const menuSections = useActiveSections(location.zoneId);
  const allMenuSections = useAllMenuSections();
  const { searchText } = useSearch();
  const hasSearchText = searchText.trim().length > 0;
  const sectionsToSearch = hasSearchText ? allMenuSections : menuSections;
  const sections = useMemo(() => filterItemsOnSearch(sectionsToSearch, searchText), [sectionsToSearch, searchText]);
  const filteredSections = useMemo(
    () =>
      sections.filter((section) => section.items.length > 0 || section.subsections.some((sub) => sub.items.length > 0)),
    [sections]
  );
  const { shouldExpandSearchBar } = useSearch();

  const [selectedSubsectionId, setSelectedSubsectionId] = useState<string | undefined>();
  const [selectedCategoryId, setSelectedCategory] = useState<string | undefined>(filteredSections[0]?.sectionId);
  const [isSubsectionsExpanded, setIsSubsectionsExpanded] = useState(false);
  const selectedCategory =
    filteredSections.find((section) => section.sectionId === selectedCategoryId) || filteredSections[0];

  // Handle barcode scanner functionality
  // Pass filtered sections for fallback search matching
  // Barcode matching uses active sections (respects opening hours)
  useBarcodeScanner({ filteredSections, zoneId: location.zoneId });
  useBundleDefinitions();

  const selectedSubsections = useMemo(() => selectedCategory?.subsections ?? [], [selectedCategory]);

  const visibleSubsections = useMemo(
    () => (hasSearchText ? selectedSubsections.filter((sub) => sub.items.length > 0) : selectedSubsections),
    [hasSearchText, selectedSubsections]
  );

  const itemsclass = classNames('relative col-span-full mx-4 tablet:mx-0 pb-4', {
    'desktop:col-span-6 tablet:col-span-6': !shouldExpandSearchBar,
    'desktop:col-span-5 tablet:col-span-5': shouldExpandSearchBar,
    'h-main overflow-auto tablet:py-2': !isPhone,
    'flex-1 min-h-0 overflow-auto': isPhone,
  });

  const selectedSubsection = selectedSubsections.find((section) => section.id === selectedSubsectionId);

  const menuSectionsOverflowRef = useRef<HTMLDivElement>(null);
  const subsectionsOverflowRef = useRef<HTMLDivElement>(null);
  const [isMenuSectionsOverflowing, setIsMenuSectionsOverflowing] = useState(false);
  const [isSubsectionsOverflowing, setIsSubsectionsOverflowing] = useState(false);

  useEffect(() => {
    const sectionHeaderElement = menuSectionsOverflowRef.current;
    if (!sectionHeaderElement) {
      setIsMenuSectionsOverflowing(false);
      return;
    }

    const checkOverflow = () => {
      setIsMenuSectionsOverflowing(sectionHeaderElement.scrollWidth > sectionHeaderElement.clientWidth);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(sectionHeaderElement);
    window.addEventListener('resize', checkOverflow);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [menuSections, selectedCategoryId, shouldExpandSearchBar, isPhone]);

  useEffect(() => {
    const subsectionHeaderElement = subsectionsOverflowRef.current;
    if (!subsectionHeaderElement) {
      setIsSubsectionsOverflowing(false);
      return;
    }

    const checkOverflow = () => {
      setIsSubsectionsOverflowing(subsectionHeaderElement.scrollWidth > subsectionHeaderElement.clientWidth);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(subsectionHeaderElement);
    window.addEventListener('resize', checkOverflow);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [selectedSubsections, selectedSubsectionId, selectedCategoryId]);

  const categoryItems = selectedCategory?.items ?? [];
  const subsectionItems = selectedSubsections.flatMap((section) => section.items);
  const displayedItems =
    config.showSubsections && subsectionItems.length > 0
      ? selectedSubsection
        ? selectedSubsection.items
        : sortAllProducts({ parentSectionItems: categoryItems, subsections: selectedSubsections })
      : categoryItems;

  const showOverflowIcon = isMenuSectionsOverflowing || isSubsectionsOverflowing || isSubsectionsExpanded;
  const handleToggleSubsectionsExpanded = () => {
    setIsSubsectionsExpanded((prev) => !prev);
  };
  const handleSectionSelected = (sectionId: string) => {
    setSelectedCategory(sectionId);
    setIsSubsectionsExpanded(false);
  };
  const handleSubsectionSelected = (subsectionId?: string) => {
    setSelectedSubsectionId(subsectionId);
    setIsSubsectionsExpanded(false);
  };

  return (
    <main className="relative bg-grey-lightest h-main flex flex-col gap-2 tablet:h-auto tablet:min-h-main tablet:grid tablet:grid-cols-12 tablet:auto-rows-auto tablet:gap-2">
      <MenuSections
        menuSections={filteredSections}
        onSectionSelected={handleSectionSelected}
        selectedSectionId={selectedCategory?.sectionId}
        overflowRef={menuSectionsOverflowRef}
        showOverflowIcon={showOverflowIcon}
        onOverflowIconClick={handleToggleSubsectionsExpanded}
        isExpanded={isSubsectionsExpanded}
      />
      {isPhone && config.showSubsections && visibleSubsections.length > 0 && (
        <div className="mx-4">
          <SubsectionHeader
            subsections={visibleSubsections}
            onSubsectionSelected={handleSubsectionSelected}
            selectedSubsectionId={selectedSubsectionId}
            overflowRef={subsectionsOverflowRef}
            isExpanded={isSubsectionsExpanded}
          />
        </div>
      )}
      <div className={itemsclass}>
        {config.showSubsections && !isPhone && showOverflowIcon && (
          <ExpandHeaderButton
            isPhone={false}
            onExpandHeaderClick={handleToggleSubsectionsExpanded}
            isExpanded={isSubsectionsExpanded}
          />
        )}
        {!isPhone && (
          <div className="flex items-start gap-2">
            {config.showSubsections && visibleSubsections.length > 0 && (
              <SubsectionHeader
                subsections={visibleSubsections}
                onSubsectionSelected={handleSubsectionSelected}
                selectedSubsectionId={selectedSubsectionId}
                overflowRef={subsectionsOverflowRef}
                isExpanded={isSubsectionsExpanded}
              />
            )}
          </div>
        )}
        <Items items={displayedItems || []} />
      </div>
      <ErrorBoundary title="Checkout">
        {!isPhone && <CheckoutSideBar />}

        {isPhone && <CartButton onClick={() => navigate(createCartUrl(location.locationId))} />}
      </ErrorBoundary>
      <BundleConversionDialog />
    </main>
  );
};

export default Menu;
